
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import "./LearningGrowthPage.css";

export default function LearningGrowthPage() {
  const { progressId, userId } = useParams();

   

  // =========================================
  // FORM STATE
  // =========================================

  const [readingMinutes, setReadingMinutes] = useState("");
  const [studyingMinutes, setStudyingMinutes] = useState("");
  const [skillPracticeMinutes, setSkillPracticeMinutes] =
    useState("");
  const [courseMinutes, setCourseMinutes] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // =========================================
  // DATE HELPERS
  // =========================================

  const getToday = () => {
    const today = new Date();

    return today.toISOString().split("T")[0];
  };

  const getCurrentWeek = () => {
    const today = new Date();

    const day = today.getDay();

    const daysSinceMonday =
      day === 0 ? 6 : day - 1;

    const monday = new Date(today);

    monday.setDate(
      today.getDate() - daysSinceMonday
    );

    const sunday = new Date(monday);

    sunday.setDate(
      monday.getDate() + 6
    );

    return {
      weekStart: monday
        .toISOString()
        .split("T")[0],

      weekEnd: sunday
        .toISOString()
        .split("T")[0],
    };
  };

  const date = getToday();

  const {
    weekStart,
    weekEnd,
  } = getCurrentWeek();

  // =========================================
  // GET RESULTS
  // =========================================

  const result = useQuery(
    api.progress.getLearningGrowthResults,
    progressId && userId
      ? {
          userId,
          progressId,
          date,
          weekStart,
          weekEnd,
        }
      : "skip"
  );

  // =========================================
  // MUTATION
  // =========================================

  const calculateLearningGrowth = useMutation(
    api.progress.calculateLearningGrowth
  );

  // =========================================
  // SAFE NUMBER
  // =========================================

  const safeNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : 0;
  };

  // =========================================
  // SUBMIT
  // =========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!progressId || !userId) {
      setError(
        "Missing progress or user information."
      );
      return;
    }

    if (
      readingMinutes === "" ||
      studyingMinutes === "" ||
      skillPracticeMinutes === "" ||
      courseMinutes === ""
    ) {
      setError(
        "Please enter all of your daily Learning & Growth information."
      );
      return;
    }

    const reading = Number(readingMinutes);
    const studying = Number(studyingMinutes);
    const skillPractice = Number(
      skillPracticeMinutes
    );
    const course = Number(courseMinutes);

    if (
      !Number.isFinite(reading) ||
      !Number.isFinite(studying) ||
      !Number.isFinite(skillPractice) ||
      !Number.isFinite(course)
    ) {
      setError("Please enter valid numbers.");
      return;
    }

    if (
      reading < 0 ||
      studying < 0 ||
      skillPractice < 0 ||
      course < 0
    ) {
      setError("Minutes cannot be negative.");
      return;
    }

    try {
      setLoading(true);

      await calculateLearningGrowth({
        userId,
        progressId,

        date,

        readingMinutes: reading,
        studyingMinutes: studying,
        skillPracticeMinutes: skillPractice,
        courseMinutes: course,

        weekStart,
        weekEnd,
      });

      // Clear form
      setReadingMinutes("");
      setStudyingMinutes("");
      setSkillPracticeMinutes("");
      setCourseMinutes("");
    } catch (err) {
      console.error(
        "Learning & Growth Error:",
        err
      );

      setError(
        err?.message ||
          "Failed to save Learning & Growth."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // LOADING
  // =========================================

  if (result === undefined) {
    return (
      <div className="learning-growth-page">
        <div className="learning-growth-container">
          <div className="learning-growth-loading">
            <div className="learning-growth-eyebrow">
              PERSONAL DEVELOPMENT
            </div>

            <h1 className="learning-growth-title">
              Learning & Growth
            </h1>

            <p className="learning-growth-loading-text">
              Loading your Learning & Growth results...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // =========================================
  // RESULTS
  // =========================================

  const todayResult = result?.daily;

  const weeklyResult = result?.weekly;

  // =========================================
  // DEBUG
  // =========================================

  

  // =========================================
  // TODAY'S LEARNING CHART
  // =========================================

  const dailyChartData = [
    {
      name: "Reading",
      minutes: safeNumber(
        todayResult?.readingMinutes
      ),
    },

    {
      name: "Studying",
      minutes: safeNumber(
        todayResult?.studyingMinutes
      ),
    },

    {
      name: "Skill Practice",
      minutes: safeNumber(
        todayResult?.skillPracticeMinutes
      ),
    },

    {
      name: "Course",
      minutes: safeNumber(
        todayResult?.courseMinutes
      ),
    },
  ];

  // =========================================
  // WEEKLY DAILY PROGRESS
  // =========================================

  const weeklyProgressChartData =
    Array.isArray(result?.dailyRecords)
      ? result.dailyRecords.map(
          (record, index) => ({
            day:
              record?.day ||
              record?.date ||
              `Day ${index + 1}`,

            score: Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  record?.dailyScore
                )
              )
            ),
          })
        )
      : [];

  // =========================================
  // SCORE COMPARISON
  // =========================================

  const scoreChartData = [
    {
      name: "Tracking",
      score: Math.max(
        0,
        Math.min(
          100,
          safeNumber(
            weeklyResult?.trackingScore
          )
        )
      ),
    },

    {
      name: "Activities",
      score: Math.max(
        0,
        Math.min(
          100,
          safeNumber(
            weeklyResult?.activityScore
          )
        )
      ),
    },

    {
      name: "Final",
      score: Math.max(
        0,
        Math.min(
          100,
          safeNumber(
            weeklyResult?.finalScore
          )
        )
      ),
    },
  ];

  // =========================================
  // WEEKLY AVERAGE MINUTES
  // =========================================

  const weeklyMinutesChartData = [
    {
      name: "Reading",
      minutes: safeNumber(
        weeklyResult?.averageReadingMinutes
      ),
    },

    {
      name: "Studying",
      minutes: safeNumber(
        weeklyResult?.averageStudyingMinutes
      ),
    },

    {
      name: "Skill Practice",
      minutes: safeNumber(
        weeklyResult?.averageSkillPracticeMinutes
      ),
    },

    {
      name: "Courses",
      minutes: safeNumber(
        weeklyResult?.averageCourseMinutes
      ),
    },
  ];

  // =========================================
  // PAGE
  // =========================================

  return (
    <div className="learning-growth-page">
      <div className="learning-growth-container">

        {/* =====================================
            HEADER
        ===================================== */}

        <div className="learning-growth-header">
          <div className="learning-growth-eyebrow">
            PERSONAL DEVELOPMENT
          </div>

          <h1 className="learning-growth-title">
            Learning & Growth
          </h1>

          <p className="learning-growth-subtitle">
            Track today's learning habits and see
            how they contribute to your weekly
            progress.
          </p>
        </div>

        {/* =====================================
            DAILY INPUT
        ===================================== */}

        <form
          onSubmit={handleSubmit}
          className="learning-growth-form-card"
        >
          <div className="learning-growth-form-header">
            <div>
              <span className="learning-growth-form-label">
                DAILY INPUT
              </span>

              <h2>
                Build Your Learning Habits
              </h2>

              <p>
                Record how much time you spent
                learning today.
              </p>
            </div>
          </div>

          <div className="learning-growth-form">

            {/* READING */}

            <div className="learning-growth-input-group">
              <label>
                Reading Minutes
              </label>

              <input
                type="number"
                min="0"
                value={readingMinutes}
                onChange={(e) =>
                  setReadingMinutes(
                    e.target.value
                  )
                }
                placeholder="Example: 30"
                className="learning-growth-input"
              />
            </div>

            {/* STUDYING */}

            <div className="learning-growth-input-group">
              <label>
                Studying Minutes
              </label>

              <input
                type="number"
                min="0"
                value={studyingMinutes}
                onChange={(e) =>
                  setStudyingMinutes(
                    e.target.value
                  )
                }
                placeholder="Example: 45"
                className="learning-growth-input"
              />
            </div>

            {/* SKILL PRACTICE */}

            <div className="learning-growth-input-group">
              <label>
                Skill Practice Minutes
              </label>

              <input
                type="number"
                min="0"
                value={skillPracticeMinutes}
                onChange={(e) =>
                  setSkillPracticeMinutes(
                    e.target.value
                  )
                }
                placeholder="Example: 30"
                className="learning-growth-input"
              />
            </div>

            {/* COURSE */}

            <div className="learning-growth-input-group">
              <label>
                Course Minutes
              </label>

              <input
                type="number"
                min="0"
                value={courseMinutes}
                onChange={(e) =>
                  setCourseMinutes(
                    e.target.value
                  )
                }
                placeholder="Example: 30"
                className="learning-growth-input"
              />
            </div>
          </div>

          {/* ERROR */}

          {error && (
            <div className="learning-growth-error">
              {error}
            </div>
          )}

          {/* BUTTON */}

          <button
            type="submit"
            disabled={loading}
            className="learning-growth-submit"
          >
            {loading
              ? "Saving..."
              : "Save Learning & Growth"}
          </button>
        </form>

        {/* =====================================
            RESULTS
        ===================================== */}

        <div className="learning-growth-results">

          {/* ===================================
              TODAY SCORE
          =================================== */}

          <div className="learning-growth-card learning-growth-daily-score">
            <div>
              <span className="learning-growth-card-eyebrow">
                TODAY
              </span>

              <h2 className="learning-growth-section-title">
                Today's Learning & Growth Score
              </h2>

              <p className="learning-growth-score-date">
                {date}
              </p>
            </div>

            <p className="learning-growth-score-value">
              {Math.round(
                safeNumber(
                  todayResult?.dailyScore
                )
              )}

              <span>
                /100
              </span>
            </p>
          </div>

          {/* ===================================
              TODAY ACTIVITY CHART
          =================================== */}

          <div className="learning-growth-card learning-growth-chart-card">
            <h2 className="learning-growth-section-title">
              Today's Learning Activity
            </h2>

            <div className="learning-growth-chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={dailyChartData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 10,
                    bottom: 30,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <YAxis
                    allowDecimals={false}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0b120e",
                      border: "1px solid #2f513c",
                      borderRadius: "12px",
                      color: "#f8fafc",
                      boxShadow:
                        "0 12px 30px rgba(0, 0, 0, 0.35)",
                    }}
                    labelStyle={{
                      color: "#86efac",
                      fontWeight: 700,
                    }}
                  />

                  <Bar
                    dataKey="minutes"
                    name="Minutes"
                    fill="#22c55e"
                    radius={[7, 7, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ===================================
              WEEKLY SCORE CARDS
          =================================== */}

          <div className="learning-growth-weekly-section">
            <div className="learning-growth-section-heading">
              <span className="learning-growth-card-eyebrow">
                WEEKLY OVERVIEW
              </span>

              <h2 className="learning-growth-large-title">
                Weekly Learning & Growth Score
              </h2>
            </div>

            <div className="learning-growth-score-grid">

              {/* TRACKING */}

              <div className="learning-growth-score-card tracking-card">
                <p>
                  Tracking Score
                </p>

                <p className="learning-growth-small-score">
                  {Math.round(
                    safeNumber(
                      weeklyResult?.trackingScore
                    )
                  )}

                  <span>
                    /100
                  </span>
                </p>
              </div>

              {/* ACTIVITY */}

              <div className="learning-growth-score-card activity-card">
                <p>
                  Activity Score
                </p>

                <p className="learning-growth-small-score">
                  {Math.round(
                    safeNumber(
                      weeklyResult?.activityScore
                    )
                  )}

                  <span>
                    /100
                  </span>
                </p>
              </div>

              {/* FINAL */}

              <div className="learning-growth-score-card final-card">
                <p>
                  Final Score
                </p>

                <p className="learning-growth-small-score">
                  {Math.round(
                    safeNumber(
                      weeklyResult?.finalScore
                    )
                  )}

                  <span>
                    /100
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* ===================================
              WEEKLY PROGRESS CHART
          =================================== */}

          <div className="learning-growth-card learning-growth-chart-card">
            <h2 className="learning-growth-section-title">
              Learning & Growth Weekly Progress
            </h2>

            {weeklyProgressChartData.length === 0 ? (
              <div className="learning-growth-empty">
                No daily progress data available
                yet.
              </div>
            ) : (
              <div className="learning-growth-chart-container">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <BarChart
                    data={
                      weeklyProgressChartData
                    }
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#26372d"
                    />

                    <XAxis
                      dataKey="day"
                      interval={0}
                      tick={{
                        fill: "#94a3b8",
                        fontSize: 12,
                      }}
                      axisLine={{
                        stroke: "#33443a",
                      }}
                      tickLine={{
                        stroke: "#33443a",
                      }}
                    />

                    <YAxis
                      domain={[0, 100]}
                      allowDecimals={false}
                      tick={{
                        fill: "#94a3b8",
                        fontSize: 12,
                      }}
                      axisLine={{
                        stroke: "#33443a",
                      }}
                      tickLine={{
                        stroke: "#33443a",
                      }}
                    />

                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0b120e",
                        border: "1px solid #2f513c",
                        borderRadius: "12px",
                        color: "#f8fafc",
                        boxShadow:
                          "0 12px 30px rgba(0, 0, 0, 0.35)",
                      }}
                      labelStyle={{
                        color: "#86efac",
                        fontWeight: 700,
                      }}
                    />

                    <Bar
                      dataKey="score"
                      name="Daily Score"
                      fill="#84cc16"
                      radius={[7, 7, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ===================================
              SCORE COMPARISON
          =================================== */}

          <div className="learning-growth-card learning-growth-chart-card">
            <h2 className="learning-growth-section-title">
              Score Comparison
            </h2>

            <div className="learning-growth-chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={scoreChartData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 10,
                    bottom: 20,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <YAxis
                    domain={[0, 100]}
                    allowDecimals={false}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0b120e",
                      border: "1px solid #2f513c",
                      borderRadius: "12px",
                      color: "#f8fafc",
                      boxShadow:
                        "0 12px 30px rgba(0, 0, 0, 0.35)",
                    }}
                    labelStyle={{
                      color: "#86efac",
                      fontWeight: 700,
                    }}
                  />

                  <Bar
                    dataKey="score"
                    name="Score"
                    fill="#f59e0b"
                    radius={[7, 7, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ===================================
              WEEKLY AVERAGE CHART
          =================================== */}

          <div className="learning-growth-card learning-growth-chart-card">
            <h2 className="learning-growth-section-title">
              Average Learning Time
            </h2>

            <div className="learning-growth-chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={
                    weeklyMinutesChartData
                  }
                  margin={{
                    top: 10,
                    right: 20,
                    left: 10,
                    bottom: 30,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <YAxis
                    allowDecimals={false}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0b120e",
                      border: "1px solid #2f513c",
                      borderRadius: "12px",
                      color: "#f8fafc",
                      boxShadow:
                        "0 12px 30px rgba(0, 0, 0, 0.35)",
                    }}
                    labelStyle={{
                      color: "#86efac",
                      fontWeight: 700,
                    }}
                  />

                  <Bar
                    dataKey="minutes"
                    name="Minutes per Day"
                    fill="#22c55e"
                    radius={[7, 7, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ===================================
              WEEKLY DETAILS
          =================================== */}

          <div className="learning-growth-card learning-growth-details-card">
            <h2 className="learning-growth-section-title">
              Weekly Details
            </h2>

            <div className="learning-growth-details-grid">

              <p className="learning-growth-detail-item">
                Days measured:{" "}
                <strong>
                  {weeklyResult?.daysMeasured ??
                    0}
                </strong>
              </p>

              <p className="learning-growth-detail-item">
                Learning & Growth suggestions:{" "}
                <strong>
                  {weeklyResult?.suggestionCount ??
                    0}
                </strong>
              </p>

              <p className="learning-growth-detail-item">
                Completed activities:{" "}
                <strong>
                  {weeklyResult?.completedActivities ??
                    0}
                </strong>
              </p>

              <p className="learning-growth-detail-item">
                Average reading:{" "}
                <strong>
                  {safeNumber(
                    weeklyResult?.averageReadingMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p className="learning-growth-detail-item">
                Average studying:{" "}
                <strong>
                  {safeNumber(
                    weeklyResult?.averageStudyingMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p className="learning-growth-detail-item">
                Average skill practice:{" "}
                <strong>
                  {safeNumber(
                    weeklyResult?.averageSkillPracticeMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p className="learning-growth-detail-item">
                Average course time:{" "}
                <strong>
                  {safeNumber(
                    weeklyResult?.averageCourseMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
